import { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { QuizEditor } from "../components/QuizEditor";
import { DepartmentPicker } from "../components/DepartmentPicker";
import {
  Microphone, StopCircle, UploadSimple, Sparkle, CheckCircle,
  FileText, PencilSimple, FilmSlate, NotePencil, ListChecks, Trash,
} from "@phosphor-icons/react";

const CATEGORIES = ["Kitchen", "Bar", "Service", "Housekeeping", "Safety", "Training", "Recipe"];
const TABS = [
  { key: "voice", label: "Voice Recording", icon: Microphone },
  { key: "document", label: "Document Upload", icon: FileText },
  { key: "manual", label: "Manual Text", icon: PencilSimple },
  { key: "edit", label: "Edit SOPs", icon: NotePencil },
];

const EMPTY_MANUAL = { title: "", category: "Training", summary: "", stepsText: "", tags: "", image_url: "", video_url: "", quiz: [] };

export default function SOPStudioPage() {
  const [tab, setTab] = useState("voice");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [published, setPublished] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [sops, setSops] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deptIds, setDeptIds] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const docRef = useRef(null);

  useEffect(() => {
    if (tab === "edit") api.get("/sops").then((r) => setSops(r.data)).catch(() => {});
  }, [tab]);

  const removeSop = async (sop) => {
    if (!window.confirm(`Delete SOP "${sop.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sops/${sop.id}`);
      toast.success("SOP deleted");
      setSops((prev) => prev.filter((x) => x.id !== sop.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (s) => {
    setManual({
      title: s.title, category: s.category, summary: s.summary || "",
      stepsText: (s.steps || []).join("\n"), tags: (s.tags || []).join(", "),
      image_url: s.image_url || "", video_url: s.video_url || "",
      quiz: JSON.parse(JSON.stringify(s.quiz || [])),
    });
    setEditingId(s.id);
    setTab("manual");
  };

  const handleDraft = (data, source) => {
    setDraft({ ...data.sop, video_url: data.sop.video_url || "" });
    setTranscript(source);
    setPublished(false);
    toast.success("SOP draft generated — review, edit the quiz, then publish");
  };

  const processAudio = async (blob, filename) => {
    setBusy(true); setDraft(null);
    try {
      const form = new FormData();
      form.append("file", blob, filename);
      const { data } = await api.post("/sops/voice-to-sop", form, { headers: { "Content-Type": "multipart/form-data" } });
      handleDraft(data, data.transcript);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const processDocument = async (file) => {
    setBusy(true); setDraft(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/sops/from-document", form, { headers: { "Content-Type": "multipart/form-data" } });
      handleDraft(data, data.source_excerpt);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        processAudio(new Blob(chunksRef.current, { type: "audio/webm" }), "recording.webm");
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied — upload an audio file instead");
    }
  };

  const publishDraft = async () => {
    try {
      await api.post("/sops", { ...draft, published: true, department_ids: deptIds });
      setPublished(true);
      toast.success("SOP published to the vault");
    } catch (e) { toast.error(apiError(e)); }
  };

  const publishManual = async () => {
    if (!manual.title.trim()) return toast.error("Title is required");
    const steps = manual.stepsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (steps.length === 0) return toast.error("Add at least one step (one per line)");
    setBusy(true);
    const payload = {
      title: manual.title, category: manual.category, summary: manual.summary,
      steps, tags: manual.tags.split(",").map((t) => t.trim()).filter(Boolean),
      image_url: manual.image_url, video_url: manual.video_url,
      quiz: manual.quiz.filter((q) => q.question.trim()), published: true,
      department_ids: deptIds,
    };
    try {
      if (editingId) {
        await api.put(`/sops/${editingId}`, payload);
        toast.success("SOP updated in the vault");
        setEditingId(null);
      } else {
        await api.post("/sops", payload);
        toast.success("SOP published to the vault");
      }
      setManual(EMPTY_MANUAL);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 rise-in max-w-3xl" data-testid="sop-studio-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Unified SOP Studio</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">SOP Studio</h1>
        <p className="text-slate-500 mt-3">Turn voice notes, documents or plain text into structured brand SOPs with quizzes — edit anything before publishing.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`sop-studio-tab-${t.key}`}
            onClick={() => { setTab(t.key); if (t.key !== "manual") { setEditingId(null); if (editingId) setManual(EMPTY_MANUAL); } }}
            className={`h-12 px-5 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${
              tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            <t.icon size={18} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "edit" && (
        <div className="space-y-3" data-testid="sop-edit-list">
          {sops.map((s) => (
            <div key={s.id} data-testid={`sop-edit-row-${s.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3 border-l-4 border-l-emerald-500">
              <div className="flex-1 min-w-0">
                <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{s.category} · v{s.version || 1}</span>
                <h3 className="font-heading font-bold text-lg leading-snug">{s.title}</h3>
                <p className="text-xs text-slate-400 font-semibold mt-1 flex items-center gap-1.5">
                  <ListChecks size={14} /> {s.steps?.length || 0} steps · {s.quiz?.length || 0} quiz Qs{s.video_url ? " · video attached" : ""}
                </p>
              </div>
              <button data-testid={`sop-delete-button-${s.id}`} onClick={() => removeSop(s)}
                className="h-12 w-12 rounded-full border border-red-200 text-red-500 flex items-center justify-center tap-press hover:bg-red-50 shrink-0" title="Delete SOP">
                <Trash size={18} />
              </button>
              <button data-testid={`sop-edit-button-${s.id}`} onClick={() => startEdit(s)}
                className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800 shrink-0">
                <NotePencil size={18} weight="duotone" /> Edit SOP
              </button>
            </div>
          ))}
          {sops.length === 0 && <p className="text-slate-400">No published SOPs yet — create one in the other tabs first.</p>}
        </div>
      )}

      {tab === "voice" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col sm:flex-row gap-4">
          {!recording ? (
            <button data-testid="voice-record-button" onClick={startRecording} disabled={busy}
              className="flex-1 h-16 rounded-full bg-slate-900 text-white font-heading font-bold flex items-center justify-center gap-3 tap-press hover:bg-slate-800 disabled:opacity-50">
              <Microphone size={24} weight="duotone" /> Record instructions
            </button>
          ) : (
            <button data-testid="voice-stop-button" onClick={() => { mediaRef.current?.stop(); setRecording(false); }}
              className="flex-1 h-16 rounded-full bg-red-500 text-white font-heading font-bold flex items-center justify-center gap-3 tap-press">
              <StopCircle size={24} weight="fill" className="pulse-dot" /> Stop & generate
            </button>
          )}
          <button data-testid="voice-upload-button" onClick={() => audioRef.current?.click()} disabled={busy || recording}
            className="flex-1 h-16 rounded-full border-2 border-slate-200 font-heading font-bold text-slate-700 flex items-center justify-center gap-3 tap-press hover:border-slate-900 disabled:opacity-50">
            <UploadSimple size={24} /> Upload audio file
          </button>
          <input ref={audioRef} type="file" accept=".mp3,.m4a,.wav,.webm,.mp4,.mpga,.mpeg,.ogg,.flac" className="hidden"
            data-testid="voice-file-input"
            onChange={(e) => e.target.files?.[0] && processAudio(e.target.files[0], e.target.files[0].name)} />
        </div>
      )}

      {tab === "document" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <button data-testid="document-upload-button" onClick={() => docRef.current?.click()} disabled={busy}
            className="w-full h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-500 font-bold tap-press hover:border-slate-900 disabled:opacity-50">
            <FileText size={32} weight="duotone" /> Upload .pdf, .txt or .md — AI converts it into an SOP
          </button>
          <input ref={docRef} type="file" accept=".pdf,.txt,.md" className="hidden" data-testid="document-file-input"
            onChange={(e) => e.target.files?.[0] && processDocument(e.target.files[0])} />
        </div>
      )}

      {tab === "manual" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="manual-sop-form">
          {editingId && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="sop-editing-banner">
              <p className="text-sm font-bold text-amber-800">Editing published SOP — changes update the vault immediately</p>
              <button data-testid="sop-cancel-edit" onClick={() => { setEditingId(null); setManual(EMPTY_MANUAL); }}
                className="text-xs font-bold text-amber-700 underline tap-press">Cancel edit</button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="manual-title-input" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })}
              placeholder="SOP / recipe title" className="h-12 px-4 rounded-xl border border-slate-200" />
            <select data-testid="manual-category-select" value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })}
              className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <input data-testid="manual-summary-input" value={manual.summary} onChange={(e) => setManual({ ...manual, summary: e.target.value })}
            placeholder="Short summary" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
          <textarea data-testid="manual-steps-input" value={manual.stepsText} onChange={(e) => setManual({ ...manual, stepsText: e.target.value })}
            placeholder={"Steps — one per line\n1. Chill the glass\n2. Build over ice…"} rows={5}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="manual-image-input" value={manual.image_url} onChange={(e) => setManual({ ...manual, image_url: e.target.value })}
              placeholder="Image URL (optional)" className="h-12 px-4 rounded-xl border border-slate-200" />
            <div className="relative">
              <FilmSlate size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="manual-video-input" value={manual.video_url} onChange={(e) => setManual({ ...manual, video_url: e.target.value })}
                placeholder="Video link (YouTube / .mp4, optional)" className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200" />
            </div>
          </div>
          <input data-testid="manual-tags-input" value={manual.tags} onChange={(e) => setManual({ ...manual, tags: e.target.value })}
            placeholder="Tags, comma separated" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Custom quiz (optional)</p>
            <QuizEditor quiz={manual.quiz} onChange={(quiz) => setManual({ ...manual, quiz })} />
          </div>
          <DepartmentPicker value={deptIds} onChange={setDeptIds} testId="sop-dept-picker" />
          <button data-testid="manual-publish-button" onClick={publishManual} disabled={busy}
            className="h-14 px-8 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
            {busy ? (editingId ? "Updating…" : "Publishing…") : (editingId ? "Update SOP" : "Publish to SOP Vault")}
          </button>
        </div>
      )}

      {busy && tab !== "manual" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center gap-4" data-testid="sop-studio-processing">
          <Sparkle size={28} weight="duotone" className="text-emerald-500 pulse-dot" />
          <div>
            <p className="font-heading font-bold">Transcribing & structuring…</p>
            <p className="text-sm text-slate-500">Whisper / document parser → GPT-5.4-mini → SOP + quiz</p>
          </div>
        </div>
      )}

      {draft && (
        <div className="space-y-6" data-testid="sop-studio-result">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">Source</p>
            <p className="text-slate-700 leading-relaxed italic line-clamp-4">"{transcript}"</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8 border-l-4 border-l-emerald-500">
            <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{draft.category}</span>
            <h2 className="font-heading text-2xl font-bold mt-1">{draft.title}</h2>
            <p className="text-slate-600 mt-2">{draft.summary}</p>
            <div className="mt-5 space-y-2">
              {draft.steps.map((s, i) => (
                <div key={i} className="flex gap-3 text-base">
                  <span className="font-heading font-extrabold text-emerald-600 w-6">{i + 1}.</span>
                  <p>{s}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Quiz — edit or override the AI questions</p>
              <QuizEditor quiz={draft.quiz || []} onChange={(quiz) => setDraft({ ...draft, quiz })} />
            </div>
            {published ? (
              <div className="mt-6 flex items-center gap-2 text-emerald-600 font-bold" data-testid="sop-studio-published-badge">
                <CheckCircle size={22} weight="fill" /> Published to vault
              </div>
            ) : (
              <button data-testid="sop-studio-publish-button" onClick={publishDraft}
                className="mt-6 h-14 px-8 rounded-full bg-emerald-600 text-white font-heading font-bold tap-press hover:bg-emerald-700">
                Publish to SOP Vault
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
